// Changes the app made itself (a message read or unread, starred or not, moved or trashed, a whole folder marked
// read), shown in the listings at once: the app does not wait for Yahoo, the action is done in the background
// (outbox.perform). A change shows over the rows listed before the server had it; a row listed after that is shown as
// the server lists it. So a listing that reloads while an action is still being done keeps showing it, and nothing
// flickers back on screen between a change and the listing that has it. The actions waiting on the device (outbox.ts:
// in the outbox, or kept for their turn by a page that was closed) show the same way over the rows the server lists,
// until they leave it: the server has not had them, so back online its listings would otherwise show the mail as it
// was until they are sent.

import type { MessageRow } from "./api";

export interface Change {
  /** The read flag cleared (true) or set (false). */
  unread?: boolean;
  /** The star (IMAP's \Flagged) set (true) or cleared (false). */
  flagged?: boolean;
  /** Gone from the listing (moved, trashed). */
  removed?: boolean;
}

/** The flags a change sets, which the change made last decides. */
type Flag = "unread" | "flagged";
const FLAGS: Flag[] = ["unread", "flagged"];

export interface Override extends Change {
  /** Changes still being done. */
  pending: number;
  /** When the last of them was done (tick()); Infinity while one is being done. */
  at: number;
  /** When each flag was last changed, to tell which came last: a change on the message, one waiting on the device or
   * one on its whole folder. */
  since: Partial<Record<Flag, number>>;
}

/** By message id, or by folder name in lower case for the folders marked read. */
export type Overrides = Map<string, Override>;

/** A listing row with the moment its page was asked for (tick()), and whether it came from the offline copy, which
 * shows the actions waiting in the outbox already. */
export type Listed = MessageRow & { listedAt: number; copy?: boolean };

const NONE: Overrides = new Map();

let clock = 0;

/** The app's own clock: the order in which changes are made, actions done and listings asked for. */
export function tick(): number {
  return ++clock;
}

/** A change made now, being done. A move or trash of the message that was done already is not carried on: the
 * message is listed where it went, which is where this change is made (the actions waiting in the outbox last as long
 * as the session, and a message moved offline and later marked unread in its new folder would otherwise be hidden
 * there until the unread was sent). */
export function begin(overrides: Overrides, key: string, change: Change, now = tick()): Overrides {
  const old = overrides.get(key);
  const kept = old?.removed && old.pending === 0 ? { ...old, removed: false } : old;
  const since = { ...old?.since };
  for (const f of FLAGS) if (change[f] !== undefined) since[f] = now;
  const next = new Map(overrides);
  next.set(key, { ...kept, ...change, pending: (old?.pending ?? 0) + 1, at: Infinity, since });
  return next;
}

/** The change is done (ok), or it is not and is taken back (unless a later change set something else since). */
export function settle(overrides: Overrides, key: string, change: Change, ok: boolean, now = tick()): Overrides {
  const old = overrides.get(key);
  if (!old) return overrides;
  const o: Override = { ...old, pending: Math.max(0, old.pending - 1) };
  if (!ok) {
    for (const f of FLAGS) if (change[f] !== undefined && o[f] === change[f]) delete o[f];
    if (change.removed && o.removed) delete o.removed;
  }
  if (o.pending === 0) o.at = now;
  const next = new Map(overrides);
  next.set(key, o);
  return next;
}

/** A row as the app shows it, with the changes the server had not made when it was listed; null when it left the
 * listing. `folder`: the folder the listing is of (its rows carry that folder's flags); null for a search across
 * folders, whose rows are read when any of their folders is. `waiting`: the changes of the actions waiting on the
 * device, by message id (outbox.ts), shown over the rows the server listed. Where several set the read flag or the
 * star, the one made last wins. */
export function shown(row: Listed, messages: Overrides, folders: Overrides, folder: string | null, waiting = NONE): MessageRow | null {
  const after = (o: Override | undefined) => (o && o.at > row.listedAt ? o : null);
  const flags: Partial<Record<Flag, boolean>> = {};
  const latest: Record<Flag, number> = { unread: -1, flagged: -1 };
  const take = (o: Override | null) => {
    for (const f of FLAGS) {
      const at = o?.since[f];
      if (o?.[f] !== undefined && at !== undefined && at > latest[f]) {
        flags[f] = o[f];
        latest[f] = at;
      }
    }
  };
  for (const o of [after(messages.get(row.id)), row.copy ? null : after(waiting.get(row.id))]) {
    if (o?.removed) return null;
    take(o);
  }
  for (const name of folder ? [folder] : row.folders) take(after(folders.get(name.toLowerCase())));
  return FLAGS.every((f) => flags[f] === undefined || flags[f] === row[f]) ? row : { ...row, ...flags };
}
